import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ArrowRightIcon, NavCloseIcon } from 'assets/icons';
import { GenericTimeline, SidePanel } from 'components';
import { InvoiceTimeline } from '../invoices.model';
import { InvoiceHistoryRouteState } from 'routes/types';
import { DEFAULT_SIDE_PANEL_ROUTE_STATE, FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { StatusIndicator } from '../constants';
import { TimelineActions } from '../types';
import { useSendInvoiceReminderMutation } from '../api';

const InvoiceHistoryPanel: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    rootInvoiceId,
    invoiceId,
    rootJobId,
    rootReceiptId,
    rootCustomerId,
    rootDisputeId,
    rootEstimateId,
    rootPage
  } = useParams();

  const { handleSidePanelClose } = useOutletContext<{ handleSidePanelClose: () => void }>();
  const currentTeam = useAppSelector(selectCurrentTeam);

  const [showBackArrow, setShowBackArrow] = useState(false);
  const [timelineEvents, setTimelineEvents] = useState<InvoiceTimeline[]>([]);
  const [invoiceStatusIndicator, setInvoiceStatusIndicator] = useState<string>('');

  const [sendInvoiceReminder, { isLoading: isSendingReminder }] = useSendInvoiceReminderMutation();

  useEffect(() => {
    let routeStateTimeout;
    const routeState = location.state as InvoiceHistoryRouteState;

    if (routeState?.fromSidePanel) {
      setShowBackArrow(true);
    }

    // Set timeline events from route state
    if (routeState?.timelineEvents) {
      setTimelineEvents(routeState.timelineEvents);
    }

    // Set invoice status indicator from route state
    if (routeState?.invoiceStatusIndicator) {
      setInvoiceStatusIndicator(routeState.invoiceStatusIndicator);
    }

    if (routeState?.shouldAnimate) {
      routeStateTimeout = setTimeout(() => {
        navigate(location.pathname, { replace: true, state: DEFAULT_SIDE_PANEL_ROUTE_STATE });
      }, 800);
    }
    return () => {
      clearTimeout(routeStateTimeout);
    };
  }, [location]);

  const onSidePanelClose = () => {
    if (showBackArrow) {
      navigate(-1);
    } else {
      handleSidePanelClose();
    }
  };

  const handleShareInvoice = () => {
    // Check if invoice is NOT fully paid to determine the correct route
    if (invoiceStatusIndicator !== StatusIndicator.FULLY_PAID) {
      // Navigate to share-pending-invoice routes (for non-fully-paid invoices)
      if (rootInvoiceId) {
        if (invoiceId) {
          navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-pending-invoice/${invoiceId}`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        } else {
          navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-pending-invoice`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        }
      } else if (rootPage) {
        navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/share-pending-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootReceiptId) {
        navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/share-pending-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootCustomerId) {
        navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/share-pending-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootDisputeId) {
        navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/share-pending-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootJobId) {
        navigate(`/jobs/${currentTeam?.id}/${rootJobId}/share-pending-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootEstimateId) {
        navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/share-pending-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    } else {
      // Navigate to regular share-invoice routes (for fully paid invoices)
      if (rootInvoiceId) {
        if (invoiceId) {
          navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-invoice/${invoiceId}`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        } else {
          navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/share-invoice`, {
            state: FROM_SIDE_PANEL_ROUTE_STATE
          });
        }
      } else if (rootPage) {
        navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/share-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootReceiptId) {
        navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/share-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootCustomerId) {
        navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/share-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootDisputeId) {
        navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/share-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootJobId) {
        navigate(`/jobs/${currentTeam?.id}/${rootJobId}/share-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      } else if (rootEstimateId) {
        navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/share-invoice/${invoiceId}`, {
          state: FROM_SIDE_PANEL_ROUTE_STATE
        });
      }
    }
  };

  const handleSendReminder = () => {
    if (!currentTeam?.id || !(invoiceId || rootInvoiceId)) {
      return;
    }

    sendInvoiceReminder({
      teamId: currentTeam.id.toString(),
      invoiceId: String(invoiceId ? invoiceId : rootInvoiceId)
    });
  };

  const handleButtonClick = (action: string) => {
    if (action.toLowerCase() === TimelineActions.SHARE_INVOICE) {
      handleShareInvoice();
    } else if (action.toLowerCase() === TimelineActions.SEND_REMINDER) {
      handleSendReminder();
    }
  };

  return (
    <SidePanel isOpen={true} onClose={onSidePanelClose} shouldAnimate={location?.state?.shouldAnimate}>
      <div className="flex h-full w-96 max-w-sm flex-col">
        <>
          <div className="flex items-center gap-2 border-b border-borderGray bg-white py-5 px-4">
            <div onClick={onSidePanelClose} className="cursor-pointer">
              {showBackArrow ? (
                <ArrowRightIcon className="rotate-180" id="invoice-history-panel-back-arrow" />
              ) : (
                <NavCloseIcon id="invoice-history-panel-close" />
              )}
            </div>
            <div id="invoice-history-panel-head" className="text-17px font-semibold text-headingGray">
              History
            </div>
          </div>

          <div
            className="customNormalScroll flex max-w-sm grow flex-col gap-3 overflow-y-auto 
            overflow-x-hidden p-4">
            <>
              {timelineEvents && timelineEvents.length > 0 ? (
                <GenericTimeline
                  events={timelineEvents}
                  onButtonClick={handleButtonClick}
                  className=""
                  isActionLoading={isSendingReminder}
                />
              ) : (
                <></>
              )}
            </>
          </div>
        </>
      </div>
    </SidePanel>
  );
};

export default InvoiceHistoryPanel;
